import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormText,
} from 'reactstrap';

import { closeAllModals } from '../actions/modalActions';
import { showInfo, showError } from '../actions/notificationActions';
import approvals from '../services/approvals.service';
import DataTable from './DataTable';

// Entry point of the maker-checker flow. Opened by the HTTP interceptor when a
// guarded action returns `403 approval-required`. The captureToken in `meta` is
// opaque and is passed back verbatim to create the pending request.
class ApprovalRequiredModal extends Component {
  state = {
    loading: false,
    submitted: false, // request created (or already exists) -> show success view
    created: null, // the created/related request object (for the tracking link)
    resourceUri: '', // optional review link the checker can open
  };

  componentDidUpdate(prevProps) {
    // Reset local state each time the modal is (re)opened. Default the review
    // link to the current detail page so the checker can inspect the resource.
    if (!prevProps.open && this.props.open) {
      this.setState({
        loading: false,
        submitted: false,
        created: null,
        resourceUri: this.defaultResourceUri(),
      });
    }
  }

  // Same-origin relative path of the page the maker is acting on.
  defaultResourceUri = () => {
    const { pathname, search } = this.props.location || {};
    return `${pathname || ''}${search || ''}`;
  };

  close = () => {
    this.props.dispatch(closeAllModals());
  };

  goToMyRequests = () => {
    this.close();
    this.props.history.push('/my-requests');
  };

  requestApproval = () => {
    const meta = this.props.meta || {};
    if (!meta.captureToken) {
      this.props.dispatch(
        showError('Missing approval token — please retry the original action.')
      );
      return;
    }

    this.setState({ loading: true });
    approvals
      .createRequest(meta.captureToken, (this.state.resourceUri || '').trim())
      .then((res) => {
        this.props.dispatch(showInfo(res.message || 'Submitted for approval'));
        this.setState({ loading: false, submitted: true, created: res.data });
      })
      .catch((err) => {
        this.setState({ loading: false });
        const status = err && err.status;
        const message = (err && err.message) || '';

        if (status === 'validation-error') {
          // Most commonly an expired/invalid capture token (~30 min lifetime).
          this.props.dispatch(
            showError(
              message ||
                'Invalid or expired capture token. Please retry the original action.'
            )
          );
          this.close();
        } else if (/already/i.test(message)) {
          // 409 conflict — a request for this operation already exists. Treat as
          // informational and link the user to the existing/related request.
          this.props.dispatch(showInfo(message));
          this.setState({
            submitted: true,
            created: (err && err.data) || null,
          });
        } else {
          this.props.dispatch(
            showError(message || 'Could not submit this action for approval.')
          );
        }
      });
  };

  renderSummary() {
    const meta = this.props.meta || {};
    const req = meta.request || {};
    return (
      <div>
        <p>
          This action needs a <strong>second approver</strong> before it can
          run. The person who approves it must be a different authorised admin
          and will confirm with a one-time email code.
        </p>
        <DataTable card={false}>
          <tbody>
            <tr>
              <td className="text-muted">Action</td>
              <td>
                <strong>{meta.action || '—'}</strong>
              </td>
            </tr>
            <tr>
              <td className="text-muted">Request</td>
              <td>
                <code>
                  {(req.method || '').toUpperCase()} {req.path || ''}
                </code>
              </td>
            </tr>
          </tbody>
        </DataTable>

        <FormGroup className="mt-2">
          <Label for="approval-resource-uri">Resource link (optional)</Label>
          <Input
            id="approval-resource-uri"
            type="text"
            placeholder="/orders/123"
            value={this.state.resourceUri}
            disabled={this.state.loading}
            onChange={(e) => this.setState({ resourceUri: e.target.value })}
          />
          <FormText color="muted">
            A link the approver can open to review what's changing. Defaults to
            this page; use a relative path (e.g. <code>/orders/123</code>) or a
            full https URL. Leave blank to skip.
          </FormText>
        </FormGroup>
      </div>
    );
  }

  renderSuccess() {
    const created = this.state.created;
    return (
      <div>
        <p>
          <span role="img" aria-label="success">
            ✅
          </span>{' '}
          Your request has been submitted for approval. You'll be notified once
          another admin approves or declines it.
        </p>
        {created && created.reference && (
          <p className="text-muted">
            Reference: <code>{created.reference}</code>
          </p>
        )}
        <p>You can track its progress under My Approval Requests.</p>
      </div>
    );
  }

  render() {
    const { open } = this.props;
    const { loading, submitted } = this.state;

    return (
      <Modal isOpen={!!open} toggle={this.close}>
        <ModalHeader toggle={this.close}>Approval required</ModalHeader>
        <ModalBody>
          {submitted ? this.renderSuccess() : this.renderSummary()}
        </ModalBody>
        <ModalFooter>
          {submitted ? (
            <span>
              <Button color="secondary" onClick={this.close}>
                Close
              </Button>{' '}
              <Button color="primary" onClick={this.goToMyRequests}>
                View My Requests
              </Button>
            </span>
          ) : (
            <span>
              <Button color="secondary" disabled={loading} onClick={this.close}>
                Cancel
              </Button>{' '}
              <Button
                color="primary"
                disabled={loading}
                onClick={this.requestApproval}
              >
                {loading ? 'Submitting…' : 'Request approval'}
              </Button>
            </span>
          )}
        </ModalFooter>
      </Modal>
    );
  }
}

export default withRouter(
  connect((state) => ({
    open: state.modals.approvalRequired,
    meta: state.modals.data,
  }))(ApprovalRequiredModal)
);
